"use client";

import React, { useState, useEffect } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex items-center gap-6">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-5 w-20 animate-pulse rounded-md bg-muted" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative py-2 text-sm font-medium text-foreground transition-colors hover:text-primary">
                {item.label}
                <span className="absolute bottom-0 left-1/2 h-[2px] w-0 -translate-x-1/2 bg-primary transition-all duration-300 ease-out group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
        <NavigationMenuItem>
          <Link to="/ConnectPage">
            <Button className="bg-gradient-to-r from-primary to-secondary font-default text-sm font-bold tracking-wider text-primary-foreground transition-all hover:scale-105 hover:shadow-[0_0_25px_rgba(0,255,255,0.5)] rounded-md">
              BOOK CONSULTATION
            </Button>
          </Link>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="mt-8 flex flex-col gap-6">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-6 w-32 animate-pulse rounded-md bg-muted" />)}
      </div>;
  }
  return <nav className="mt-8 flex flex-col gap-6">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} data-index={i} className="text-lg font-default font-medium text-foreground transition-colors hover:text-primary">
          {item.label}
        </Link>)}
      <Link to="/ConnectPage" className="mt-4">
        <Button className="w-full bg-gradient-to-r from-primary to-secondary font-default font-bold text-primary-foreground rounded-md">
          Book Consultation
        </Button>
      </Link>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <header data-section-id="264693" id="header" className={`dark sticky top-0 z-50 w-full transition-all duration-500 ${isScrolled ? "border-b border-white/10 bg-background/70 shadow-lg backdrop-blur-lg" : "bg-background"}`}>
      <div className="container mx-auto flex h-20 items-center justify-between px-4 md:px-6">
        
        {}
        <div className="flex flex-1 md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu" className="hover:bg-white/5">
                <Menu className="h-6 w-6 text-foreground" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[300px] sm:w-[400px] bg-background border-white/10">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <div className="mt-6 flex flex-col">
                <Link to="/HomePage" className="mb-8 inline-block">
                  <WvcLogo className="h-8 w-auto text-primary" />
                </Link>
                <MenuProvider menu_id="42">
                  <MobileNav />
                </MenuProvider>
              </div>
            </SheetContent>
          </Sheet>
        </div>

        {}
        <div className="flex flex-1 justify-center md:justify-start">
          <Link to="/HomePage" className="inline-block transition-transform hover:scale-105">
            <WvcLogo className="h-8 w-auto" />
          </Link>
        </div>

        {}
        <div className="hidden flex-1 justify-end md:flex">
          <MenuProvider menu_id="42">
            <DesktopNav />
          </MenuProvider>
        </div>

      </div>
    </header>;
}